import React from 'react';
import { ArrowRight, Globe, Lock, Route, Send, Server, ShieldCheck, ShoppingBag } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PlatformLogo } from '@/components/commerce/platform-logo';

/**
 * THE LEFT HALF OF THE SIGN-IN PAGE (owner, 25 Oct 2026: "Unleash the true
 * power of server side tracking and beyond - make this the new title ... the
 * 4 cards, can we make it much better?").
 *
 * The title is the owner's words. The sentence under it is unchanged: it names
 * the app and its purpose, which Google's OAuth branding reviewers look for on
 * this page. The old ticked list and the four plain cards said the same four
 * things twice; they are one set of tiles now, each with a small drawn
 * specimen of what it does - generic, no customer's data, no claimed number.
 * Colour is honey and ink, plus the platforms' own logos (owner, 25 Oct 2026:
 * first "too many color spots" - the pink and mint background blobs and the
 * green chips, which are gone - then, all grey, "very dull"). Honey carries
 * the icons, the highlight and the result of each specimen.
 */

const chip = 'inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--card-border))] bg-background/80 px-2 py-1 font-mono text-[12px] text-foreground';
const arrow = <ArrowRight className="size-3 shrink-0 text-muted-foreground/70" aria-hidden="true" />;

function Touch({ children, title }) {
  return (
    <span title={title} className="grid size-6 place-items-center rounded-md border border-[hsl(var(--card-border))] bg-white shadow-sm dark:bg-background">
      {children}
    </span>
  );
}

function Pill({ children, on }) {
  return (
    <span className={cn('whitespace-nowrap rounded-full px-1.5 py-0.5 text-[12px] font-medium',
      on ? 'bg-honey text-honey-950' : 'border border-[hsl(var(--card-border))] bg-background/80 text-muted-foreground')}>
      {children}
    </span>
  );
}

const TILES = [
  {
    Icon: ShieldCheck, title: 'Server-side tracking', line: 'First party, on your own domain. Orders the pixel missed, recovered.',
    specimen: (
      <span className={chip}><Lock className="size-3 text-honey-600" aria-hidden="true" />bees.yourstore.com</span>
    ),
  },
  {
    Icon: Server, title: 'Server GTM hosting', line: 'Your own container on your domain, with no extra vendor.',
    specimen: (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className={chip}><PlatformLogo platform="sgtm" className="size-3.5" />GTM-XXXXXXX</span>
        {arrow}
        <span className={chip}>your domain</span>
      </span>
    ),
  },
  {
    Icon: Route, title: 'Journeys and attribution', line: 'Every touch behind every order, not just the last click.',
    specimen: (
      <span className="flex items-center gap-1.5">
        <Touch title="Meta ad"><PlatformLogo platform="meta" className="size-3.5" /></Touch>{arrow}
        <Touch title="Google ad"><PlatformLogo platform="google_ads" className="size-3.5" /></Touch>{arrow}
        <Touch title="Came back directly"><Globe className="size-3.5 text-muted-foreground" /></Touch>{arrow}
        <span className="grid size-6 place-items-center rounded-md bg-honey text-honey-950 shadow-sm" title="Order"><ShoppingBag className="size-3.5" /></span>
      </span>
    ),
  },
  {
    Icon: Send, title: 'Offline & CRM conversions', line: 'Qualified leads and offline sales, sent back to your ads.',
    specimen: (
      <span className="flex items-center gap-1">
        <Pill>Lead</Pill>{arrow}<Pill>Qualified</Pill>{arrow}<Pill on>Sale</Pill>{arrow}
        <span className="inline-flex shrink-0 items-center gap-1">
          <PlatformLogo platform="meta" className="size-4" />
          <PlatformLogo platform="google_ads" className="size-4" />
        </span>
      </span>
    ),
  },
];

export function LoginShowcase() {
  return (
    <>
      <h2 className="mt-7 text-balance text-center text-[30px] font-semibold leading-[1.12] tracking-[-0.02em] lg:text-left lg:text-[40px]">
        Unleash the true power of{' '}
        <span className="bg-[linear-gradient(transparent_60%,rgba(229,167,11,0.38)_60%)] px-0.5">server side tracking</span>{' '}
        and beyond
      </h2>
      <p className="mt-4 text-center text-[15px] leading-relaxed text-muted-foreground lg:text-left">
        Cookiebees tracks every visit on your own domain, enriches each event with the customer data ad platforms match on,
        and measures the result against real orders.
      </p>
      {/* On a phone the tiles drop away and the form follows the sentence. */}
      <div className="mt-7 hidden grid-cols-2 gap-3 lg:grid">
        {TILES.map(({ Icon, title, line, specimen }) => (
          <div key={title}
            className="group flex flex-col rounded-2xl border border-[hsl(var(--card-border))] bg-card p-4 shadow-[0_1px_2px_rgba(16,46,40,0.04)] transition-[box-shadow,transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-honey/40 hover:shadow-[0_14px_30px_-20px_rgba(229,167,11,0.6)] motion-reduce:transition-none motion-reduce:hover:translate-y-0">
            <span className="flex items-center gap-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-honey-100 text-honey-700 dark:bg-honey-900/40 dark:text-honey-300">
                <Icon className="size-4" />
              </span>
              <span className="text-[14.5px] font-semibold leading-snug tracking-[-0.01em]">{title}</span>
            </span>
            <span className="mt-2 text-[13.5px] leading-snug text-muted-foreground">{line}</span>
            <span className="mt-auto pt-3.5">{specimen}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export default LoginShowcase;
